import type { ApiErrorShape } from '../store/axiosBaseQuery.js'

/**
 * The sentence the server already wrote.
 *
 * Every surface answers with `{ error: { code, message } }`, the base query
 * normalises it, and the message is written in the product's voice for the
 * reader rather than for a log — *"Somebody has to be able to manage
 * administrators. Give another administrator that permission first."*
 *
 * The desk was throwing all of it away and guessing instead. Two of the
 * guesses were plainly wrong: the invite panel answered every failure with
 * "They may not have an account here yet", and taking away your **own**
 * access — which the server refuses with "You cannot remove your own access."
 * — was reported as "This is the last person who can manage administrators."
 * A wrong reason is worse than no reason, because somebody acts on it.
 *
 * So: read the server's words when there are any, and fall back to the
 * screen's own sentence when the request never arrived at all.
 */
export function whatWentWrong(error: unknown, fallback: string): string {
  const e = error as ApiErrorShape | undefined
  if (!e || typeof e !== 'object') return fallback
  /* A connection that never opened has no server message, only ours, and
     "check your network" is more use than anything a screen could add. */
  if (e.status === 0 && e.message) return e.message
  return typeof e.message === 'string' && e.message.trim() && e.code !== 'INTERNAL'
    ? e.message
    : fallback
}
